import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { 
  Copy, 
  Send, 
  Zap, 
  Check,
  AlertCircle,
  Loader2,
  Download,
  X
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pv`;

// Quick items configuration for multi-select
const QUICK_ITEMS = [
  { id: "kit_frizione_3", label: "Kit frizione 3 pz.", icon: "🔧" },
  { id: "kit_frizione_4", label: "Kit frizione 4 pz.", icon: "🔧" },
  { id: "tagliando", label: "Tagliando", icon: "🛢️" },
  { id: "filtro_carburante", label: "Filtro carburante", icon: "⛽" },
  { id: "kit_distribuzione", label: "Kit distribuzione", icon: "⚙️" },
  { id: "kit_distribuzione_pacqua", label: "Distribuzione + p.acqua", icon: "⚙️" },
  { id: "dischi_ant", label: "Dischi ant.", icon: "🛞" },
  { id: "dischi_post", label: "Dischi post.", icon: "🛞" },
  { id: "pastiglie_ant", label: "Pastiglie ant.", icon: "🛞" },
  { id: "pastiglie_post", label: "Pastiglie post.", icon: "🛞" },
  { id: "batteria", label: "Batteria", icon: "🔋" },
  { id: "turbina", label: "Turbina", icon: "💨" },
  { id: "candele", label: "Candele", icon: "⚡" },
  { id: "motorino_avviamento", label: "Motorino avviamento", icon: "🔌" },
  { id: "alternatore", label: "Alternatore", icon: "🔌" },
  { id: "altro", label: "Altro", icon: "📝" },
];

export default function PreventivoVeloce() {
  const { token: urlToken } = useParams();
  
  // Get token from URL or localStorage (for PWA standalone mode)
  const [token, setToken] = useState(() => {
    // If we have a token in URL, use it and save it
    if (urlToken) {
      localStorage.setItem('pv-workshop-token', urlToken);
      return urlToken;
    }
    // Otherwise try to get from localStorage (PWA opened from home screen)
    return localStorage.getItem('pv-workshop-token') || null;
  });
  
  // Module status
  const [moduleStatus, setModuleStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // PWA install prompt
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  
  // Form state
  const [plate, setPlate] = useState("");
  const [selectedItems, setSelectedItems] = useState([]); // Multi-select array
  const [otherDescription, setOtherDescription] = useState("");
  const [urgent, setUrgent] = useState(false);
  const [notes, setNotes] = useState("");
  
  // Submission state
  const [submitting, setSubmitting] = useState(false);
  
  // Update token when URL changes
  useEffect(() => {
    if (urlToken && urlToken !== token) {
      localStorage.setItem('pv-workshop-token', urlToken);
      setToken(urlToken);
    }
  }, [urlToken, token]);

  // Register service worker and setup PWA
  useEffect(() => {
    // Add PWA manifest link
    const existingManifest = document.querySelector('link[rel="manifest"]');
    if (existingManifest) {
      existingManifest.href = '/pv-manifest.json';
    } else {
      const manifestLink = document.createElement('link');
      manifestLink.rel = 'manifest';
      manifestLink.href = '/pv-manifest.json';
      document.head.appendChild(manifestLink);
    }
    
    // Set theme color
    let themeColor = document.querySelector('meta[name="theme-color"]');
    if (!themeColor) {
      themeColor = document.createElement('meta');
      themeColor.name = 'theme-color';
      document.head.appendChild(themeColor);
    }
    themeColor.content = '#f97316';
    
    // Register service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/pv-sw.js', { scope: '/r/' })
        .then((registration) => {
          console.log('PV Service Worker registered:', registration.scope);
        })
        .catch((err) => {
          console.log('PV Service Worker registration failed:', err);
        });
    }
    
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }
    
    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      // Show banner only if not already installed and not dismissed
      const dismissed = localStorage.getItem('pv-install-dismissed');
      if (!dismissed) {
        setShowInstallBanner(true);
      }
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowInstallBanner(false);
      setInstallPrompt(null);
      toast.success('App installata con successo!');
    });
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  // Handle install button click
  const handleInstall = async () => {
    if (!installPrompt) return;
    
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setInstallPrompt(null);
      setShowInstallBanner(false);
    }
  };
  
  // Dismiss install banner
  const dismissInstallBanner = () => {
    setShowInstallBanner(false);
    localStorage.setItem('pv-install-dismissed', 'true');
  };

  // Check module status on mount
  useEffect(() => {
    checkModuleStatus();
  }, [token]);

  const checkModuleStatus = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/module-status?workshop_token=${token}`);
      const data = await res.json();
      setModuleStatus(data);
      
      if (!data.enabled) {
        setError(data.reason || "Modulo non abilitato");
      }
    } catch (err) {
      setError("Errore di connessione. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  // Handle plate input change
  const handlePlateChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    setPlate(value);
  };

  // Copy plate to clipboard
  const copyPlate = async () => {
    if (!plate) return;
    
    try {
      await navigator.clipboard.writeText(plate);
      toast.success("Targa copiata!");
    } catch (err) {
      toast.error("Impossibile copiare");
    }
  };

  // Toggle item selection (multi-select)
  const toggleItem = (itemId) => {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  // Submit request and open WhatsApp directly
  const handleSubmit = async () => {
    // Validate
    if (!plate || plate.length < 5) {
      toast.error("Inserisci una targa valida");
      return;
    }
    
    if (selectedItems.length === 0) {
      toast.error("Seleziona almeno un ricambio");
      return;
    }
    
    if (selectedItems.includes("altro") && !otherDescription.trim()) {
      toast.error("Inserisci una descrizione per 'Altro'");
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Create request
      const res = await fetch(`${API}/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plate,
          selected_items: selectedItems,
          other_description: selectedItems.includes("altro") ? otherDescription : null,
          urgent,
          notes: notes || null,
          workshop_token: token
        })
      });
      
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || "Errore invio richiesta");
      }
      
      const requestData = await res.json();
      
      // Open WhatsApp directly
      const phone = requestData.supplier_phone;
      const text = encodeURIComponent(requestData.whatsapp_text);
      const whatsappUrl = `https://wa.me/${phone}?text=${text}`;
      
      // Reset form for next request
      setPlate("");
      setSelectedItems([]);
      setOtherDescription("");
      setUrgent(false);
      setNotes("");
      
      // Show success toast
      toast.success("Richiesta creata! Apertura WhatsApp...");
      
      // Open WhatsApp
      window.location.href = whatsappUrl;
      
    } catch (err) {
      toast.error(err.message || "Errore invio richiesta");
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }
  
  // No token available (PWA opened without ever visiting with token)
  if (!token) {
    return (
      <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center p-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Accesso Richiesto</h1>
          <p className="text-zinc-400 mb-6">
            Per utilizzare Richiesta Rapida Ricambi, accedi tramite il link fornito dalla tua officina.
          </p>
          <p className="text-zinc-500 text-sm">
            Contatta il tuo fornitore per ricevere il link di accesso.
          </p>
        </div>
      </div>
    );
  }

  // Error/disabled state
  if (error || !moduleStatus?.enabled) {
    return (
      <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center p-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Modulo Non Disponibile</h1>
          <p className="text-zinc-400 mb-6">{error || moduleStatus?.reason}</p>
          <Button
            variant="outline"
            onClick={() => window.location.reload()}
            className="border-zinc-700 text-zinc-300"
          >
            Riprova
          </Button>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="min-h-screen bg-zinc-950" data-testid="pv-form-page">
      {/* Install Banner */}
      {showInstallBanner && !isInstalled && (
        <div className="bg-gradient-to-r from-orange-600 to-orange-500 px-4 py-3">
          <div className="max-w-md mx-auto flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <Download className="w-5 h-5 text-white flex-shrink-0" />
              <p className="text-white text-sm font-medium truncate">
                Installa l'app per accesso rapido
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                onClick={handleInstall}
                size="sm"
                className="bg-white text-orange-600 hover:bg-orange-50 h-8 px-3 text-xs font-bold"
              >
                Installa
              </Button>
              <button
                onClick={dismissInstallBanner}
                className="text-white/70 hover:text-white p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="sticky top-0 z-10 bg-zinc-950/95 backdrop-blur border-b border-zinc-800 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-bold text-white">Richiesta Rapida Ricambi</h1>
            {isInstalled && (
              <span className="bg-green-500/20 text-green-400 text-[10px] px-1.5 py-0.5 rounded font-medium">
                APP
              </span>
            )}
          </div>
          <span className="text-xs text-zinc-500">{moduleStatus.workshop_name}</span>
        </div>
      </div>

      <div className="max-w-md mx-auto p-4 pb-32">
        {/* Plate Section */}
        <div className="mb-6">
          <label className="text-sm text-zinc-500 mb-2 block">Targa veicolo</label>
          
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <input
                type="text"
                value={plate}
                onChange={handlePlateChange}
                placeholder="Inserisci targa"
                className="w-full h-14 bg-zinc-900 border border-zinc-700 rounded-lg px-4 text-white text-xl font-mono tracking-wider uppercase focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                maxLength={10}
                data-testid="pv-plate-input"
              />
            </div>
            <Button
              variant="outline"
              onClick={copyPlate}
              disabled={!plate}
              className="h-14 px-4 border-zinc-700"
              data-testid="pv-copy-plate-btn"
            >
              <Copy className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Quick Items Multi-Select Grid */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm text-zinc-500">Seleziona ricambi</label>
            <span className="text-sm text-primary font-medium">
              Selezionati: {selectedItems.length}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {QUICK_ITEMS.filter(item => item.id !== "altro").map((item) => {
              const isSelected = selectedItems.includes(item.id);
              return (
                <button
                  key={item.id}
                  onClick={() => toggleItem(item.id)}
                  className={`p-4 rounded-lg border text-left transition-all ${
                    isSelected
                      ? "bg-primary/20 border-primary text-white ring-1 ring-primary"
                      : "bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-zinc-700"
                  }`}
                  data-testid={`pv-item-${item.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{item.icon}</span>
                      <span className="text-sm font-medium">{item.label}</span>
                    </div>
                    {isSelected && (
                      <Check className="w-4 h-4 text-primary" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          
          {/* ALTRO - Pulsante grande e ben visibile */}
          <button
            onClick={() => toggleItem("altro")}
            className={`w-full mt-3 p-5 rounded-xl border-2 text-left transition-all ${
              selectedItems.includes("altro")
                ? "bg-blue-500/20 border-blue-500 text-white ring-2 ring-blue-500"
                : "bg-gradient-to-r from-zinc-900 to-zinc-800 border-blue-500/50 text-white hover:border-blue-500"
            }`}
            data-testid="pv-item-altro"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl">📝</span>
                <div>
                  <span className="text-lg font-bold block">ALTRO RICAMBIO</span>
                  <span className="text-sm text-zinc-400">Descrivi cosa ti serve</span>
                </div>
              </div>
              {selectedItems.includes("altro") && (
                <Check className="w-6 h-6 text-blue-500" />
              )}
            </div>
          </button>
          
          {/* Other description input */}
          {selectedItems.includes("altro") && (
            <div className="mt-3">
              <input
                type="text"
                value={otherDescription}
                onChange={(e) => setOtherDescription(e.target.value.slice(0, 80))}
                placeholder="Descrivi il ricambio richiesto..."
                className="w-full h-14 bg-zinc-900 border-2 border-blue-500/50 rounded-lg px-4 text-white text-lg focus:border-blue-500 outline-none"
                maxLength={80}
                autoFocus
                data-testid="pv-other-description-input"
              />
              <p className="text-xs text-zinc-600 mt-1 text-right">{otherDescription.length}/80</p>
            </div>
          )}
        </div>

        {/* Urgency Toggle */}
        <div className="mb-6">
          <button
            onClick={() => setUrgent(!urgent)}
            className={`w-full p-4 rounded-lg border flex items-center justify-between transition-all ${
              urgent
                ? "bg-yellow-500/10 border-yellow-500/50 text-yellow-500"
                : "bg-zinc-900 border-zinc-800 text-zinc-400"
            }`}
            data-testid="pv-urgent-toggle"
          >
            <div className="flex items-center gap-2">
              <Zap className={`w-5 h-5 ${urgent ? "text-yellow-500" : ""}`} />
              <span className="font-medium">Urgente oggi</span>
            </div>
            <div className={`w-12 h-6 rounded-full p-1 transition-all ${urgent ? "bg-yellow-500" : "bg-zinc-700"}`}>
              <div className={`w-4 h-4 rounded-full bg-white transition-transform ${urgent ? "translate-x-6" : ""}`} />
            </div>
          </button>
        </div>

        {/* Notes */}
        <div className="mb-6">
          <label className="text-sm text-zinc-500 mb-2 block">Note (opzionali)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, 200))}
            placeholder="Aggiungi note..."
            className="w-full h-24 bg-zinc-900 border border-zinc-800 rounded-lg p-3 text-white resize-none focus:border-primary outline-none"
            maxLength={200}
            data-testid="pv-notes-input"
          />
          <p className="text-xs text-zinc-600 mt-1 text-right">{notes.length}/200</p>
        </div>
      </div>

      {/* Sticky Submit Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-zinc-950/95 backdrop-blur border-t border-zinc-800 p-4">
        <div className="max-w-md mx-auto">
          <Button
            onClick={handleSubmit}
            disabled={submitting || !plate || selectedItems.length === 0 || (selectedItems.includes("altro") && !otherDescription.trim())}
            className="w-full h-16 bg-primary hover:bg-primary/90 text-black text-lg font-bold disabled:opacity-50"
            data-testid="pv-submit-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Invio in corso...
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                INVIA RICHIESTA ({selectedItems.length} ricambi)
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
